import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  AccountCircle as AccountCircleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Apps,
  AutoStories,
  BugReport as BugReportIcon,
  Download,
  DynamicFeed,
  Feedback as FeedbackIcon,
  GitHub as GitHubIcon,
  GridView,
  LibraryBooks,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  PrecisionManufacturing,
  RocketLaunch,
} from "@mui/icons-material";
import { useAdminPendingWork } from "../../api/adminPendingWork";
import { getUserByToken } from "../../api/userManagement";
import {
  subscribeComposerProfileUpdated,
  toComposerProfileNavSnapshot,
} from "@neuraville/react-core-pro/profile";
import { isFeagiEmbodimentIntegrationEnabled } from "../../util/feagiEmbodimentIntegration";
import { userHasAnyAdminAccessState } from "../../util/auth";
import { createAdminHandoff } from "../../api/adminSession";
import { logger } from "../../util/logger";
import { NAV_MENU_ITEM_ICON_SX } from "../../styles/navMenuStyles";
import {
  isEnterpriseTierForNav,
  subscriptionTierBadgeLabelForNav,
} from "../../util/subscriptionTierNav";
import { trackDownloadPageCtaClick } from "../../lib/analytics";
const SignupModal = dynamic(() => import("../../util/SignupModal"));
const BrainVisualizerIntro = dynamic(() => import("../BrainVisualizerIntro"));
const NavbarFeedbackSection = dynamic(() => import("./NavbarFeedbackSection"));

function FeagiLogoIcon() {
  return (
    <img
      src="/img/feagi-icon-white.png"
      alt="FEAGI"
      style={{ width: 24, height: 24, objectFit: "contain", marginRight: 8, flexShrink: 0 }}
    />
  );
}

function NrsLogoIcon() {
  return (
    <img
      src="/img/nrs-neurorobotics-studio-icon.png"
      alt="Neurorobotics Studio"
      style={{
        width: 24,
        height: 24,
        objectFit: "contain",
        marginRight: 8,
        flexShrink: 0,
        filter: "grayscale(100%)",
      }}
    />
  );
}

/**
 * In-document header links. These must render as anchors in the server HTML.
 * The hamburger menu is a MUI portal and is absent from that first response.
 */
const PRIMARY_NAV_LINKS = [
  { label: "FEAGI", path: "/feagi" },
  { label: "Embodiments", path: "/embodiments" },
  { label: "Brain Hub", path: "/hub" },
  { label: "Blog", path: "/blog" },
  { label: "Academy", path: "/academy/tutorials" },
  { label: "Docs", path: "/docs/", target: "_blank" },
];

const NAV_ACTION_HEIGHT = 36;
const GITHUB_URL = "https://github.com/feagi/feagi";
const DISCORD_URL = "https://discord.gg/RktzUqQ7Nm";

/** Shared hit target for icon-only navbar controls (admin, main menu). */
const navIconButtonSx = {
  color: "inherit",
  width: NAV_ACTION_HEIGHT,
  height: NAV_ACTION_HEIGHT,
  flexShrink: 0,
  p: 0.75,
};

/** Shared height for text/outlined navbar controls. */
const navTextButtonSx = {
  height: NAV_ACTION_HEIGHT,
  minHeight: NAV_ACTION_HEIGHT,
};

/** Secondary social actions in navbar; visually lower priority than CTA. */
const navSocialIconSx = {
  ...navIconButtonSx,
  opacity: 0.8,
  "&:hover": {
    opacity: 1,
    color: "primary.main",
  },
};

const NavbarMainNav = ({ auth, linkClick, handleSignOut, path }) => {
  const feagiEmbodimentEnabled = isFeagiEmbodimentIntegrationEnabled();
  const pathname = usePathname() || "";
  const [hasHydrated, setHasHydrated] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  /** "signup" for the primary CTA, "signin" for returning users. */
  const [authModalType, setAuthModalType] = useState("signup");
  const [menuState, setMenuState] = useState({
    anchorEl: null,
    menuId: null,
  });
  const [feedbackTypeOpen, setFeedbackTypeOpen] = useState("");
  const [bVIntroOpen, setbVIntroOpen] = useState(false);
  /** Composer display_name — distinct from Firebase auth displayName. */
  const [composerDisplayName, setComposerDisplayName] = useState(null);
  const [composerAvatar, setComposerAvatar] = useState(null);
  const authUser = hasHydrated ? auth.user : null;
  const accessToken = authUser?.accessToken;

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!accessToken) {
      setComposerDisplayName(null);
      setComposerAvatar(null);
      return;
    }
    let cancelled = false;
    getUserByToken(accessToken)
      .then(({ data }) => {
        if (!cancelled) {
          const snapshot = toComposerProfileNavSnapshot(data);
          setComposerDisplayName(snapshot.displayName);
          setComposerAvatar(snapshot.avatar);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setComposerDisplayName(null);
          setComposerAvatar(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    return subscribeComposerProfileUpdated((profile) => {
      const snapshot = toComposerProfileNavSnapshot(profile);
      setComposerDisplayName(snapshot.displayName);
      setComposerAvatar(snapshot.avatar);
    });
  }, []);

  const handleOpenMenu = (event, menuId) => {
    event.stopPropagation();
    // Store clicked element (to anchor the menu to) and the menu id so we can tell which menu is open
    setMenuState({
      anchorEl: event?.currentTarget,
      menuId,
    });
  };

  const handleCloseMenu = () => {
    setMenuState({ anchorEl: null, menuId: null });
  };

  const menuItemIconSx = NAV_MENU_ITEM_ICON_SX;
  const subscriptionTierBadge = authUser
    ? subscriptionTierBadgeLabelForNav(authUser)
    : "community";
  const subscriptionTierIsEnterprise = authUser
    ? isEnterpriseTierForNav(authUser)
    : false;
  const hasAnyAdminAccess = userHasAnyAdminAccessState(authUser) === true;
  const pendingAdminWork = useAdminPendingWork(accessToken, hasAnyAdminAccess);
  const hasPendingAdminWork = (pendingAdminWork?.total ?? 0) > 0;

  /**
   * Mint an admin session hand-off before navigating to the admin host so the
   * IAP-protected admin origin can establish a Firebase session without a second
   * manual login. Proceeds to /admin regardless: on failure the admin host simply
   * falls back to a manual login.
   */
  const handleAdminClick = async () => {
    try {
      const token = await auth?.getAccessToken?.();
      if (token) {
        await createAdminHandoff(token);
      }
    } catch (error) {
      logger("Admin hand-off creation failed; proceeding to admin login.", error);
    } finally {
      linkClick("/admin");
    }
  };

  const menuItems = [
    feagiEmbodimentEnabled &&
      path.startsWith("/brain-visualizer") && {
      label: "Quick Start",
      icon: <RocketLaunch sx={menuItemIconSx} />,
      onClick: () => setbVIntroOpen(true),
    },
    {
      label: "Neurorobotics Studio",
      icon: <NrsLogoIcon />,
      path: "/",
    },
    {
      label: "FEAGI",
      icon: <FeagiLogoIcon />,
      path: "/feagi",
    },
    {
      label: "Embodiments",
      icon: <PrecisionManufacturing sx={menuItemIconSx} />,
      path: "/embodiments",
    },
    {
      label: "Brain Hub",
      icon: <GridView sx={menuItemIconSx} />,
      path: "/hub",
    },
    {
      label: "Blog",
      icon: <DynamicFeed sx={menuItemIconSx} />,
      path: "/blog",
    },
    {
      label: "FEAGI Academy",
      icon: <AutoStories sx={menuItemIconSx} />,
      path: "/academy/tutorials",
    },
    {
      label: "Docs",
      icon: <LibraryBooks sx={menuItemIconSx} />,
      // Full navigation required — /docs/ is a static Docusaurus subapp,
      // not a Next.js route.
      path: "/docs/",
      target: "_blank",
    },
  ];

  const followNavLink = (event, item) => {
    if (!item.path || item.target === "_blank") {
      item.onClick?.();
      return;
    }
    event.preventDefault();
    linkClick(item.path);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, lg: 2.5 },
          flexShrink: 0,
        }}
      >
        <Box
          component="nav"
          aria-label="Primary"
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            gap: 2.75,
            minWidth: 0,
          }}
        >
          {PRIMARY_NAV_LINKS.map((item) => (
            <MuiLink
              key={item.path}
              href={item.path}
              target={item.target}
              rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
              underline="none"
              aria-current={pathname === item.path ? "page" : undefined}
              onClick={(event) => followNavLink(event, item)}
              sx={{
                color: pathname === item.path ? "primary.main" : "text.primary",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.01em",
                whiteSpace: "nowrap",
                opacity: pathname === item.path ? 1 : 0.8,
                "&:hover": { color: "primary.main", opacity: 1 },
              }}
            >
              {item.label}
            </MuiLink>
          ))}
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          aria-hidden
          sx={{
            display: { xs: "none", lg: "block" },
            alignSelf: "center",
            height: 18,
            borderColor: "rgba(255,255,255,0.18)",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexShrink: 0,
          }}
        >
        {/* Primary CTA */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<Download sx={{ fontSize: 15 }} />}
          onClick={() => {
            trackDownloadPageCtaClick({ source: "navbar_download_studio_button" });
            linkClick("/nrs/download");
          }}
          disabled={pathname === "/nrs/download"}
          sx={{
            ...navTextButtonSx,
            borderRadius: "100px",
            px: 1.5,
            fontSize: "0.75rem",
            fontWeight: 600,
            borderColor: "rgba(255,255,255,0.25)",
            color: "text.primary",
            letterSpacing: "0.03em",
            display: { xs: "none", sm: "flex" },
            "&:hover": {
              borderColor: "primary.main",
              color: "primary.main",
              bgcolor: "transparent",
            },
          }}
        >
          Download Studio
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          <Tooltip title="GitHub">
            <IconButton
              aria-label="Open GitHub repository"
              component="a"
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={navSocialIconSx}
            >
              <GitHubIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Discord">
            <IconButton
              aria-label="Join Discord server"
              component="a"
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={navSocialIconSx}
            >
              <img
                src="/img/discord-white.png"
                alt="Discord"
                style={{ width: 18, height: 18, objectFit: "contain" }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {/* User actions: admin shortcut + account (or sign-in) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {authUser && hasAnyAdminAccess && (
            <IconButton
              aria-label={hasPendingAdminWork ? "Admin, open review items" : "Admin"}
              onClick={handleAdminClick}
              disabled={pathname.startsWith("/admin")}
              sx={navIconButtonSx}
            >
              {hasPendingAdminWork ? (
                <Badge
                  variant="dot"
                  color="error"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />
                </Badge>
              ) : (
                <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          )}

          {!authUser ? (
            <>
              <Button
                onClick={() => {
                  setAuthModalType("signin");
                  setSignupModalOpen(true);
                }}
                type="mui"
                color="inherit"
                size="small"
                variant="text"
                sx={{
                  ...navTextButtonSx,
                  minWidth: "auto",
                  px: 1,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  opacity: 0.8,
                  display: { xs: "none", sm: "inline-flex" },
                  "&:hover": { opacity: 1, bgcolor: "transparent" },
                }}
              >
                Log in
              </Button>
              <Button
                onClick={() => {
                  setAuthModalType("signup");
                  setSignupModalOpen(true);
                }}
                type="mui"
                size="small"
                variant="contained"
                disableElevation
                sx={{
                  ...navTextButtonSx,
                  px: 1.75,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  bgcolor: "rgba(255,255,255,0.14)",
                  color: "text.primary",
                  border: "1px solid rgba(255,255,255,0.28)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.22)",
                    borderColor: "rgba(255,255,255,0.45)",
                  },
                }}
              >
                Sign up
              </Button>
            </>
          ) : (
            <>
              <Button
                color="inherit"
                aria-label="Account"
                aria-controls="account-menu"
                aria-haspopup="true"
                onClick={(event) => {
                  handleOpenMenu(event, "account-menu");
                }}
                sx={{
                  ...navTextButtonSx,
                  px: 1,
                  minWidth: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  textTransform: "none",
                  borderRadius: "5px",
                }}
              >
                {composerAvatar?.img_url ? (
                  <Avatar
                    src={composerAvatar.img_url}
                    alt={
                      composerAvatar.img_alt_text ||
                      composerDisplayName ||
                      authUser?.displayName ||
                      "Account"
                    }
                    sx={{ width: 22, height: 22 }}
                  />
                ) : (
                  <AccountCircleIcon title="account" sx={{ fontSize: 22 }} />
                )}
              <Box
                component="span"
                sx={{ display: { xs: "none", md: "inline" } }}
              >
                {composerDisplayName || authUser?.displayName || "User"}
              </Box>
              <Box
                component="span"
                sx={{
                  display: { xs: "none", sm: "inline" },
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  px: 0.75,
                  py: 0.25,
                  borderRadius: "4px",
                  backgroundColor: subscriptionTierIsEnterprise
                    ? "gold.main"
                    : "rgba(144, 202, 249, 0.15)",
                  color: subscriptionTierIsEnterprise ? "#000" : "primary.main",
                  border: "1px solid",
                  borderColor: subscriptionTierIsEnterprise
                    ? "gold.main"
                    : "rgba(144, 202, 249, 0.3)",
                }}
              >
                {subscriptionTierBadge}
              </Box>
            </Button>

            {/* Profile dropdown */}
            <Menu
              id="account-menu"
              open={menuState?.menuId === "account-menu"}
              anchorEl={menuState?.anchorEl}
              onClick={handleCloseMenu}
              onClose={handleCloseMenu}
              keepMounted={true}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              {[
                {
                  path: "/settings/profile",
                  label: "My Profile",
                  icon: PersonIcon,
                },
                {
                  path: "/settings/genomes",
                  label: "My Library",
                  icon: Apps,
                },
              ].map(({ path, label, icon: Icon }) => (
                <MenuItem
                  key={path}
                  onClick={() => linkClick(path)}
                  disabled={pathname === path}
                >
                  <Icon sx={menuItemIconSx} />
                  {label}
                </MenuItem>
              ))}

              <Divider />

              <MenuItem onClick={() => { handleCloseMenu(); setFeedbackTypeOpen("thoughts"); }}>
                <FeedbackIcon sx={menuItemIconSx} />
                Feedback
              </MenuItem>
              <MenuItem onClick={() => { handleCloseMenu(); setFeedbackTypeOpen("issues"); }}>
                <BugReportIcon sx={menuItemIconSx} />
                Bug Report
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleSignOut}>
                <LogoutIcon sx={menuItemIconSx} />
                Logout
              </MenuItem>

            </Menu>
          </>
          )}
        </Box>

        {/* Site navigation menu */}
        <IconButton
          aria-label="Main Menu"
          aria-controls="main-menu"
          aria-haspopup="true"
          onClick={(event) => {
            handleOpenMenu(event, "main-menu");
          }}
          sx={navIconButtonSx}
        >
          <MenuIcon sx={{ fontSize: 22 }} />
        </IconButton>

        {/* Main menu dropdown */}
        <Menu
          id="main-menu"
          open={menuState?.menuId === "main-menu"}
          anchorEl={menuState?.anchorEl}
          onClick={handleCloseMenu}
          onClose={handleCloseMenu}
          // keepMounted={true}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          {menuItems.map((item, index) => {
            if (!item) return null;
            return (
              <MenuItem
                key={item.path || item.label || index}
                {...(item.path
                  ? {
                      component: "a",
                      href: item.path,
                      target: item.target,
                      rel: item.target === "_blank" ? "noopener noreferrer" : undefined,
                    }
                  : {})}
                onClick={(event) => followNavLink(event, item)}
                disabled={Boolean(item.path) && pathname === item.path}
              >
                {item.icon}
                {item.label}
              </MenuItem>
            );
          })}
        </Menu>
        </Box>

        {/* {!path.startsWith("/brain-visualizer") && (
        <IconButton color="inherit" onClick={darkMode.toggle} style={{ opacity: 0.6 }} >
            {darkMode.value && <NightsStayIcon />}
            {!darkMode.value && <WbSunnyIcon />}
        </IconButton> )} */}

        {/* Feedback modal */}
        <Dialog
          open={feedbackTypeOpen}
          onClose={() => {
            setFeedbackTypeOpen("");
          }}
        >
          <NavbarFeedbackSection
            setFeedbackOpen={() => setFeedbackTypeOpen("")}
            starterType={feedbackTypeOpen}
          />
        </Dialog>

        {/* Quick Start modal */}
        {bVIntroOpen && (
          <BrainVisualizerIntro
            dialogOpen={bVIntroOpen}
            setDialogOpen={setbVIntroOpen}
            userOpened={true}
          />
        )}

      </Box>

      {/* Signup modal */}
      {signupModalOpen && (
        <SignupModal
          open={signupModalOpen}
          setOpen={setSignupModalOpen}
          authType={authModalType}
          providerChooserFirst
        />
      )}
    </>
  );
};

export default NavbarMainNav;
